import { CONFIG } from '../config';
import { supabase } from '../utils/supabaseClient';

const API_BASE_URL = CONFIG.API_URL;

const ATTRACTION_NOTIFICATION_BASE = `${API_BASE_URL}/api/attraction-notifications`;
const ALERT_NOT_FOUND_ERROR_CODE = 'ALERT_NOT_FOUND';
const ALERT_NOT_FOUND_CANCEL_MESSAGE = 'Cette alerte n’existe plus. La liste a été actualisée.';

function getVapidPublicKey(serverVapidPublicKey) {
  return String(serverVapidPublicKey || '').trim();
}

export function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = `${base64String}${padding}`
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
}

function getApplicationServerKey(serverVapidPublicKey) {
  const vapidPublicKey = getVapidPublicKey(serverVapidPublicKey);
  if (!vapidPublicKey) {
    const error = new Error('Configuration Web Push invalide : clé publique VAPID manquante.');
    error.code = 'VAPID_PUBLIC_KEY_MISSING';
    throw error;
  }

  let applicationServerKey;
  try {
    applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);
  } catch (err) {
    const error = new Error('Configuration Web Push invalide : clé publique VAPID illisible.');
    error.code = 'VAPID_PUBLIC_KEY_INVALID';
    error.cause = err;
    throw error;
  }

  if (applicationServerKey.length !== 65 || applicationServerKey[0] !== 4) {
    const error = new Error('Configuration Web Push invalide : clé publique VAPID incorrecte.');
    error.code = 'VAPID_PUBLIC_KEY_INVALID';
    throw error;
  }

  return applicationServerKey;
}

function arrayBufferToUint8Array(value) {
  if (!value) return null;
  if (value instanceof Uint8Array) return value;
  if (value instanceof ArrayBuffer) return new Uint8Array(value);
  if (ArrayBuffer.isView(value)) {
    return new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
  }
  return null;
}

function arraysAreEqual(left, right) {
  if (!left || !right || left.length !== right.length) return false;
  for (let index = 0; index < left.length; index += 1) {
    if (left[index] !== right[index]) return false;
  }
  return true;
}

function subscriptionUsesApplicationServerKey(subscription, applicationServerKey) {
  const existingKey = arrayBufferToUint8Array(subscription?.options?.applicationServerKey);
  if (!existingKey) return true;
  return arraysAreEqual(existingKey, applicationServerKey);
}

async function isBraveBrowser() {
  try {
    if (navigator.brave?.isBrave) {
      return Boolean(await navigator.brave.isBrave());
    }
  } catch {
    return false;
  }

  return false;
}

function isMobileUserAgent() {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i
    .test(navigator.userAgent || '');
}

async function createPushSubscriptionError(error) {
  const name = error?.name || '';
  const message = String(error?.message || '');
  let userMessage = message || 'Impossible de créer l’abonnement push.';

  if (name === 'AbortError' || /push service/i.test(message)) {
    const isBraveDesktop = await isBraveBrowser() && !isMobileUserAgent();
    userMessage = isBraveDesktop
      ? 'Brave Desktop bloque la création de l’abonnement push. Pour recevoir les notifications attractions, active les services Google pour les messages push dans Brave, puis efface les données du site et recharge. Si tu ne veux pas changer ce réglage Brave, utilise Chrome, Edge ou Firefox pour les notifications push.'
      : 'Le navigateur n’a pas pu créer l’abonnement push. Vérifie que les notifications du site sont autorisées, que le navigateur peut joindre son service push, puis réessaie.';
  } else if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
    userMessage = 'Les notifications sont bloquées dans le navigateur.';
  } else if (name === 'NotSupportedError') {
    userMessage = 'Ce navigateur ne prend pas en charge les notifications push.';
  }

  const enhanced = new Error(userMessage);
  enhanced.name = name || 'PushSubscriptionError';
  enhanced.code = 'PUSH_SUBSCRIBE_FAILED';
  enhanced.cause = error;
  return enhanced;
}

async function getAccessToken() {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  if (!token) {
    const error = new Error('Connexion requise');
    error.code = 'AUTH_REQUIRED';
    throw error;
  }
  return token;
}

async function requestJson(path, options = {}) {
  const token = await getAccessToken();
  const response = await fetch(`${ATTRACTION_NOTIFICATION_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.message || data.error || `Erreur HTTP ${response.status}`);
    error.status = response.status;
    error.code = data.error;
    error.details = data;
    throw error;
  }

  return data;
}

function isAlertNotFoundError(error) {
  const code = error?.code || error?.details?.error;
  return error?.status === 404 && code === ALERT_NOT_FOUND_ERROR_CODE;
}

export async function hashPushEndpoint(endpoint) {
  if (!endpoint || !window.crypto?.subtle) return null;
  const data = new TextEncoder().encode(endpoint);
  const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hashBuffer))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

export async function getExistingPushSubscription() {
  if (!('serviceWorker' in navigator)) return null;
  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration?.pushManager) return null;
  return registration.pushManager.getSubscription();
}

export async function waitForServiceWorkerReady(timeoutMs = 8000) {
  if (!('serviceWorker' in navigator)) {
    throw new Error('Service Worker indisponible');
  }

  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Service Worker indisponible')), timeoutMs);
    }),
  ]);
}

export async function subscribeBrowserToPush(serverVapidPublicKey = null) {
  const applicationServerKey = getApplicationServerKey(serverVapidPublicKey);
  const registration = await waitForServiceWorkerReady();
  if (!registration?.pushManager) {
    throw new Error('Notifications push indisponibles dans ce navigateur.');
  }

  let existing = null;
  try {
    existing = await registration.pushManager.getSubscription();
  } catch (error) {
    throw await createPushSubscriptionError(error);
  }

  if (existing) {
    if (subscriptionUsesApplicationServerKey(existing, applicationServerKey)) {
      return existing;
    }

    await existing.unsubscribe().catch(() => false);
  }

  try {
    return await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey,
    });
  } catch (error) {
    throw await createPushSubscriptionError(error);
  }
}

export async function getAttractionNotificationState(endpointHash) {
  const query = endpointHash ? `?endpointHash=${encodeURIComponent(endpointHash)}` : '';
  return requestJson(`/me${query}`, { method: 'GET' });
}

export async function createAttractionNotification({ subscription, alert, language, deviceLabel }) {
  return requestJson('/subscribe', {
    method: 'POST',
    body: JSON.stringify({
      subscription,
      alert,
      language,
      deviceLabel,
    }),
  });
}

export async function cancelAttractionNotification(alertId) {
  try {
    return await requestJson('/cancel', {
      method: 'POST',
      body: JSON.stringify({ alertId }),
    });
  } catch (error) {
    if (isAlertNotFoundError(error)) {
      return {
        success: true,
        alert: null,
        notFound: true,
        error: ALERT_NOT_FOUND_ERROR_CODE,
        message: ALERT_NOT_FOUND_CANCEL_MESSAGE,
      };
    }

    throw error;
  }
}

export async function cancelAllAttractionNotifications() {
  return requestJson('/cancel-all', { method: 'POST' });
}

export async function unsubscribeCurrentAttractionNotificationDevice(subscription) {
  return requestJson('/unsubscribe-device', {
    method: 'POST',
    body: JSON.stringify({ subscription }),
  });
}
